import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { dateValue, formatDate, formatDayMonth } from '@/lib/utils/format';
import { ChartTooltip, useChartTooltip, useSeriesKeys } from './chart-tooltip';
import {
  ChartEmpty,
  ChartLegend,
  SrTable,
  cssVars,
  f1,
  formatPct,
  type Thresholds,
} from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * TrendChart — a 0..100 value over snapshots, then today (prototype
 * `trendChart()`): the readiness total on the Overview and Snapshots tabs,
 * a criterion's score on its detail.
 *
 * Geometry as the prototype: the three gate zones as tints, gridlines at
 * 0/50/100, Go and Conditional as dashed lines labelled on the right, a
 * faint area wash under a 2px ink line, snapshot dots (today hollow), the
 * last value as an end label anchored left of the last point, and x labels
 * placed from the right so the latest always keeps its label and crowded
 * earlier ones drop. A crosshair follows the pointer to the nearest point
 * and the tooltip reads it; focus the chart and use the arrow keys for the
 * same.
 */
export interface TrendPoint {
  /** Snapshot captured_at, or today's date for the live point (ISO). */
  date: string;
  /** 0..100 from the API. */
  value: number;
  /** The live, not frozen, value: hollow dot, labelled "Today". */
  live?: boolean;
  /** Tooltip sub-line, e.g. "Conditional · posture Red". */
  sub?: string | null;
}

export interface TrendChartProps {
  points: TrendPoint[];
  thresholds: Thresholds;
  /** What the line is ("Weighted total", "UAT score"); names the chart. */
  label?: string;
  width?: number;
  height?: number;
  emptyTitle?: string;
  emptyText?: string;
  motionKey?: string | number;
  className?: string;
}

const M = { l: 34, r: 78, t: 14, b: 30 };

export function TrendChart({
  points,
  thresholds,
  label = 'Weighted total',
  width: w = 720,
  height: h = 236,
  emptyTitle = 'The trend starts with the first snapshot',
  emptyText = 'Each snapshot freezes the ratings and scores of its day.',
  motionKey,
  className,
}: TrendChartProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, show, hide } = useChartTooltip();
  const svgRef = React.useRef<SVGSVGElement>(null);
  const [active, setActive] = React.useState<number | null>(null);

  const pw = w - M.l - M.r;
  const ph = h - M.t - M.b;
  const times = points.map((p) => dateValue(p.date));
  const t0 = times[0] ?? 0;
  const t1 = times[times.length - 1] ?? 0;
  const x = (ms: number) => (t1 === t0 ? M.l + pw / 2 : M.l + ((ms - t0) / (t1 - t0)) * pw);
  const y = (v: number) => M.t + ph - (v / 100) * ph;
  const xs = times.map(x);
  const ys = points.map((p) => y(p.value));

  const tipFor = (i: number) => {
    const p = points[i];
    if (!p) return null;
    return {
      title: p.live ? 'Today (live, not frozen)' : `Snapshot ${formatDate(p.date)}`,
      value: formatPct(p.value),
      sub: p.sub ?? null,
    };
  };

  const pointAt = (i: number) => {
    const svg = svgRef.current;
    const content = tipFor(i);
    if (!svg || !content) return;
    const box = svg.getBoundingClientRect();
    const scale = box.width / w || 1;
    show(content, { x: box.left + (xs[i] ?? 0) * scale, y: box.top + (ys[i] ?? 0) * scale });
  };

  const keys = useSeriesKeys(points.length, (i) => {
    setActive(i);
    if (i === null) hide();
    else pointAt(i);
  });

  if (!points.length) {
    return (
      <ChartEmpty title={emptyTitle} className={className}>
        {emptyText}
      </ChartEmpty>
    );
  }

  const onMove = (e: React.PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const box = svg.getBoundingClientRect();
    const vx = ((e.clientX - box.left) / (box.width || 1)) * w;
    let i = 0;
    xs.forEach((xv, k) => {
      if (Math.abs(xv - vx) < Math.abs((xs[i] ?? 0) - vx)) i = k;
    });
    setActive(i);
    pointAt(i);
  };
  const onLeave = () => {
    setActive(null);
    hide();
  };

  // x labels from the right: the latest point keeps its label, earlier ones
  // closer than 44 units to the last kept label drop.
  const keep = new Set<number>();
  let lastX = Infinity;
  for (let i = points.length - 1; i >= 0; i--) {
    const xi = xs[i] ?? 0;
    if (lastX - xi >= 44) {
      keep.add(i);
      lastX = xi;
    }
  }

  const line = xs.map((a, i) => `${i ? 'L' : 'M'}${f1(a)} ${f1(ys[i] ?? 0)}`).join(' ');
  const lastI = points.length - 1;
  const lastXv = xs[lastI] ?? 0;
  const firstXv = xs[0] ?? 0;
  const area = `${line} L${f1(lastXv)} ${y(0)} L${f1(firstXv)} ${y(0)} Z`;
  const last = points[lastI];
  const g = thresholds.go;
  const c = thresholds.conditional;
  const activeX = active === null ? null : xs[active];

  return (
    <div key={runKey} className={cn('relative', animate && 'chart-anim', className)}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={`${label} by snapshot`}
        tabIndex={0}
        className="chart-svg block h-auto w-full overflow-visible rounded-[2px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600"
        {...keys.handlers}
      >
        <rect
          x={M.l}
          y={y(c)}
          width={pw}
          height={y(0) - y(c)}
          fill="var(--posture-critical-tint)"
          opacity={0.5}
        />
        <rect
          x={M.l}
          y={y(g)}
          width={pw}
          height={y(c) - y(g)}
          fill="var(--posture-at-risk-tint)"
          opacity={0.6}
        />
        <rect
          x={M.l}
          y={y(100)}
          width={pw}
          height={y(g) - y(100)}
          fill="var(--posture-healthy-tint)"
          opacity={0.55}
        />
        {[0, 50, 100].map((v) => (
          <g key={`g${v}`}>
            <line x1={M.l} x2={M.l + pw} y1={y(v)} y2={y(v)} stroke="var(--ink-100)" />
            <text x={M.l - 8} y={y(v) + 4} textAnchor="end">
              {v}
            </text>
          </g>
        ))}
        {(
          [
            [g, `Go ${g}`, 'var(--posture-healthy)'],
            [c, `Conditional ${c}`, 'var(--posture-at-risk)'],
          ] as const
        ).map(([v, text, col]) => (
          <g key={text}>
            <line
              x1={M.l}
              x2={M.l + pw}
              y1={y(v)}
              y2={y(v)}
              stroke={col}
              strokeDasharray="4 3"
              strokeWidth={1}
            />
            <text x={M.l + pw + 8} y={y(v) + 4}>
              {text}
            </text>
          </g>
        ))}
        <path d={area} fill="var(--ink-900)" opacity={0.05} />
        <path
          className="a-draw"
          d={line}
          pathLength={1}
          fill="none"
          stroke="var(--ink-900)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <line
          x1={activeX ?? 0}
          x2={activeX ?? 0}
          y1={M.t}
          y2={M.t + ph}
          stroke="var(--ink-300)"
          strokeWidth={1}
          opacity={activeX === null ? 0 : 1}
          pointerEvents="none"
        />
        {points.map((p, i) => (
          <circle
            key={`d${i}`}
            className="a-fade"
            style={cssVars({ '--i': i })}
            cx={f1(xs[i] ?? 0)}
            cy={f1(ys[i] ?? 0)}
            r={active === i ? 6 : 4.5}
            fill={p.live ? 'var(--paper-0)' : 'var(--ink-900)'}
            stroke={p.live ? 'var(--ink-900)' : 'var(--paper-0)'}
            strokeWidth={2}
          />
        ))}
        {last ? (
          <text
            className="a-fade fill-ink-900 text-[13px] font-semibold"
            style={cssVars({ '--i': points.length })}
            x={f1(lastXv - 6)}
            y={f1((ys[lastI] ?? 0) - 12)}
            textAnchor="end"
          >
            {formatPct(last.value)}
          </text>
        ) : null}
        {points.map((p, i) =>
          keep.has(i) ? (
            <text
              key={`x${i}`}
              x={f1(xs[i] ?? 0)}
              y={h - 8}
              textAnchor="middle"
              className={p.live ? 'fill-ink-900 font-semibold' : undefined}
            >
              {p.live ? 'Today' : formatDayMonth(p.date)}
            </text>
          ) : null,
        )}
        <rect
          x={M.l - 10}
          y={M.t}
          width={pw + 20}
          height={ph}
          fill="transparent"
          className="cursor-crosshair"
          onPointerMove={onMove}
          onPointerLeave={onLeave}
        />
      </svg>
      <SrTable
        caption={`${label} by snapshot`}
        head={['Snapshot', label, 'Note']}
        rows={points.map((p) => [
          p.live ? 'Today (live)' : formatDate(p.date),
          formatPct(p.value),
          p.sub ?? '',
        ])}
      />
      <ChartTooltip tip={tip} />
    </div>
  );
}

/** The trend card's key: the line, the three zones, and a trailing slot (a link). */
export function TrendLegend({
  lineLabel = 'Weighted total',
  children,
  className,
}: {
  lineLabel?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <ChartLegend
      className={cn('mt-1.5', className)}
      items={[
        { label: lineLabel, swatch: 'line', color: 'bg-ink-900' },
        { label: 'Go zone', color: 'bg-posture-healthy-tint' },
        { label: 'Conditional zone', color: 'bg-posture-at-risk-tint' },
        { label: 'No-Go zone', color: 'bg-posture-critical-tint' },
      ]}
    >
      {children}
    </ChartLegend>
  );
}
